import styled from 'styled-components';
import { Link } from 'react-router-dom';

const DemoContainer = styled.div`
  min-height: 100vh;
  padding: 6rem 2rem 4rem;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
`;

const Hero = styled.div`
  max-width: 900px;
  margin: 0 auto 4rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 3rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(135deg, var(--color-primary) 0%, var(--color-accent) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.25rem;
  color: #555;
  line-height: 1.6;
  max-width: 700px;
  margin: 0 auto;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const Section = styled.section`
  max-width: 1200px;
  margin: 0 auto 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 2rem;
  color: var(--color-primary);
  text-align: center;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const AgentCard = styled(Link)`
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s, box-shadow 0.3s;
  text-decoration: none;
  color: inherit;
  border-left: 4px solid ${props => props.$primaryColor};

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const AgentHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const AgentAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: ${props => props.$primaryColor};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
`;

const AgentInfo = styled.div`
  flex: 1;
`;

const AgentName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin: 0 0 0.25rem;
  color: ${props => props.$primaryColor};
`;

const AgentType = styled.span`
  font-size: 0.875rem;
  color: #777;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const AgentDescription = styled.p`
  font-size: 0.9375rem;
  color: #666;
  line-height: 1.6;
  margin: 1rem 0;
`;

const TryButton = styled.span`
  display: inline-block;
  padding: 0.75rem 1.5rem;
  background: ${props => props.$primaryColor};
  color: white;
  border-radius: 8px;
  font-weight: 600;
  transition: opacity 0.2s;
  text-align: center;

  ${AgentCard}:hover & {
    opacity: 0.9;
  }
`;

const agents = {
  coaching: [
    {
      id: 'demo-executive-coach',
      name: "Sarah Chen Coaching",
      type: "Executive Leadership",
      description: "Direct, strategic coaching for C-suite leaders and aspiring executives. Specializes in delegation, strategic planning, and team dynamics.",
      primaryColor: "#1B365D",
      initial: "S"
    },
    {
      id: 'demo-wellness-coach',
      name: "Maya Rodriguez Wellness",
      type: "Mindfulness & Wellbeing",
      description: "Warm, gentle guidance for building sustainable self-care routines, managing stress, and cultivating inner balance.",
      primaryColor: "#2D5F4A",
      initial: "M"
    }
  ],
  business: [
    {
      id: 'demo-bpo-support',
      name: "Pacific Customer Solutions",
      type: "BPO Tier 1 Support",
      description: "Professional customer support agent for telecom and financial services. Responds in English or Taglish. Handles billing, account management, and service activation.",
      primaryColor: "#0F4C81",
      initial: "P"
    },
    {
      id: 'demo-realestate',
      name: "Metro Property Group",
      type: "Real Estate Inquiry",
      description: "Friendly property assistant for Metro Manila, Cavite, and Cebu properties. Provides pricing, payment schemes, and schedules site visits. English or Taglish.",
      primaryColor: "#8B1A1A",
      initial: "M"
    }
  ]
};

function DemoIndex() {
  return (
    <DemoContainer>
      <Hero>
        <Title>Live AI Agent Demos</Title>
        <Subtitle>
          Experience the Valyss AI agent platform in action. Each agent has a unique personality, 
          capabilities, and branding. Try all four to see the range of what's possible.
        </Subtitle>
      </Hero>

      <Section>
        <SectionTitle>NorCal Coaching Agents</SectionTitle>
        <CardsGrid>
          {agents.coaching.map(agent => (
            <AgentCard 
              key={agent.id}
              to={`/demo/${agent.id.replace('demo-', '')}`}
              $primaryColor={agent.primaryColor}
            >
              <AgentHeader>
                <AgentAvatar $primaryColor={agent.primaryColor}>
                  {agent.initial}
                </AgentAvatar>
                <AgentInfo>
                  <AgentName $primaryColor={agent.primaryColor}>{agent.name}</AgentName>
                  <AgentType>{agent.type}</AgentType>
                </AgentInfo>
              </AgentHeader>
              <AgentDescription>{agent.description}</AgentDescription>
              <TryButton $primaryColor={agent.primaryColor}>
                Try it now →
              </TryButton>
            </AgentCard>
          ))}
        </CardsGrid>
      </Section>

      <Section>
        <SectionTitle>Philippines B2B Agents</SectionTitle>
        <CardsGrid>
          {agents.business.map(agent => (
            <AgentCard 
              key={agent.id}
              to={`/demo/${agent.id.replace('demo-', '')}`}
              $primaryColor={agent.primaryColor}
            >
              <AgentHeader>
                <AgentAvatar $primaryColor={agent.primaryColor}>
                  {agent.initial}
                </AgentAvatar>
                <AgentInfo>
                  <AgentName $primaryColor={agent.primaryColor}>{agent.name}</AgentName>
                  <AgentType>{agent.type}</AgentType>
                </AgentInfo>
              </AgentHeader>
              <AgentDescription>{agent.description}</AgentDescription>
              <TryButton $primaryColor={agent.primaryColor}>
                Try it now →
              </TryButton>
            </AgentCard>
          ))}
        </CardsGrid>
      </Section>
    </DemoContainer>
  );
}

export default DemoIndex;
